// @ts-check

// The eager entry implementation — the full-catalog boot, selected at BUILD
// TIME via the `#ce-entry` alias when the client is not listed in
// config/ce_lazy_clients.yml. Exports a boot function instead of running at
// module evaluation so the entrypoint controls the boot side-effect order
// (boot() runs after the Backbone-bridge assignment).

import { registerDefaults } from "#ce/composables/useDefaults";
import { loadConfig } from "#ce/framework/config";
import { registerAppComponents, registerComponents } from "#ce/framework/eager";
import { markAsInitialized } from "#ce/framework/index";
import { installSelectionDragGuard } from "#ce/framework/selection-drag-guard";

/**
 * Page ownership is a lazy-only concept: on an eager double-embed host page
 * both bundles boot in full, so the claim always grants and touches no
 * global state.
 *
 * @returns {true}
 */
export function claimPageOwnership() {
  return true;
}

export function boot() {
  initialize();
}

/**
 * Initializes the custom elements.
 *
 * A config failure intentionally stays an un-caught rejection: nothing
 * registers and nothing reveals — the eager build's failure shape. (The
 * lazy build instead settles started tags to fallback and reveals
 * degraded; see framework/loader.js.)
 *
 * @returns {Promise<void>}
 */
export async function initialize() {
  const config = await loadConfig();

  return await createSparkleElements(config);
}

/**
 * Registers all custom elements.
 *
 * @param {import("#ce/framework/index").SparkleElementsOptions} options - Default values for the component options.
 */
export async function createSparkleElements(options) {
  const { defaults = {}, aliases = {} } = options ?? {};

  // Restore text selections that Chrome/Safari clear when a mouse drag
  // crosses a shadow-tree boundary.
  installSelectionDragGuard();

  // Defaults must land before any component registers, so first renders see them.
  registerDefaults(defaults);

  await Promise.all([registerComponents(defaults, aliases), registerAppComponents(defaults)]);

  markAsInitialized();
}
